import { Link } from 'react-router-dom';

import { Card, CardTitle } from '@/components/ui/card';
import { buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { Organization } from '../types';

export interface OrganizationCardProps {
  organization: Organization;
  /** Extra classes so the dashboard and the list can share one component (H3). */
  className?: string;
}

/**
 * Organization card (design §8/§9) — the same component in both places. The
 * whole card is the hit area: a stretched `Link` covers the card (C1) while the
 * "Open" affordance stays a non-interactive span styled as a secondary button
 * (B2: the single primary color is reserved for page-level actions).
 */
export function OrganizationCard({ organization, className }: OrganizationCardProps) {
  const name = organization.name || organization._id;

  return (
    <Card
      title={organization.name ? undefined : organization._id}
      className={cn(
        'relative p-6 transition-shadow hover:shadow-md motion-reduce:transition-none',
        className,
      )}
    >
      <CardTitle className="truncate text-base font-semibold" title={name}>
        {name}
      </CardTitle>
      <Link
        to={`/organizations/${organization._id}`}
        className="absolute inset-0 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        aria-label={`Open ${name}`}
      />
      <span
        aria-hidden="true"
        className={cn(
          buttonVariants({ variant: 'secondary' }),
          'pointer-events-none absolute bottom-4 right-4',
        )}
      >
        Open
      </span>
    </Card>
  );
}
